import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Empty-state illustration — the shared, white-label-aware placeholder graphic
 * shown when a list / table / widget has nothing to display.
 *
 * Rendered as an **inline `<svg>`** on purpose: an `<img>` / `@img.` icon loads the
 * SVG as a separate `background-image` document, so neither `currentColor` nor CSS
 * custom properties from the host reach it — the illustration would freeze at its
 * fallback blue in every tenant. Inline keeps the SVG in the host's CSS context, so
 * the colours below resolve to the active environment's brand ramp.
 *
 * Colours are 1:1 with the Figma "Illustration/Empty" component
 * (Token Design Library, node 40004276:147637). Each SVG paint uses a two-level
 * `var()` fallback — `var(--fv-empty-illustration-*, var(--fv-color-*, #hex))` — so:
 *   • by default it resolves to the exact Figma design token (re-tints per tenant), and
 *   • a consumer can retune one placement by setting `--fv-empty-illustration-*` on ANY
 *     ancestor; because the override token is not declared on `:host`, it inherits down
 *     to the SVG unshadowed.
 * Token map: bg → Background/Neutral Secondary · surface → Background/Neutral Primary ·
 * accent → Background/Info Subtle · stroke → Content/Brand · stroke-soft → Content/Brand.
 *
 * The component fills its host box (width/height 100%) and preserves aspect ratio —
 * wrap it in a sized container, e.g. `.empty-list-block { width: 158px; height: 64px }`.
 */
@Component({
  selector: 'fv-empty-illustration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (variant()) {
      @case ('filters') {
        <svg viewBox="0 0 93 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M89.1018 0.4375C91.2547 0.4375 93 2.19923 93 4.37242C93 6.54562 91.2547 8.30735 89.1018 8.30735H66.8263C68.9793 8.30735 70.7245 10.0691 70.7245 12.2423C70.7245 14.4155 68.9793 16.1772 66.8263 16.1772H79.0778C81.2308 16.1772 82.976 17.9389 82.976 20.1121C82.976 22.2853 81.2308 24.047 79.0778 24.047H73.4121C70.6976 24.047 68.497 25.8088 68.497 27.982C68.497 29.4308 69.6108 30.7424 71.8383 31.9169C73.9912 31.9169 75.7365 33.6786 75.7365 35.8518C75.7365 38.025 73.9912 39.7867 71.8383 39.7867H25.6168C23.4638 39.7867 21.7186 38.025 21.7186 35.8518C21.7186 33.6786 23.4638 31.9169 25.6168 31.9169H3.8982C1.74529 31.9169 0 30.1552 0 27.982C0 25.8088 1.74529 24.047 3.8982 24.047H26.1737C28.3266 24.047 30.0719 22.2853 30.0719 20.1121C30.0719 17.9389 28.3266 16.1772 26.1737 16.1772H12.2515C10.0986 16.1772 8.35329 14.4155 8.35329 12.2423C8.35329 10.0691 10.0986 8.30735 12.2515 8.30735H34.5269C32.374 8.30735 30.6287 6.54562 30.6287 4.37242C30.6287 2.19923 32.374 0.4375 34.5269 0.4375H89.1018ZM89.1018 16.1772C91.2547 16.1772 93 17.9389 93 20.1121C93 22.2853 91.2547 24.047 89.1018 24.047C86.9489 24.047 85.2036 22.2853 85.2036 20.1121C85.2036 17.9389 86.9489 16.1772 89.1018 16.1772Z"
            fill="var(--fv-empty-illustration-bg, var(--fv-color-background-neutral-secondary, #dde1e9))" />
          <path
            d="M40.9311 38.6628C51.2343 38.6628 59.5868 30.2317 59.5868 19.8314C59.5868 9.43111 51.2343 1 40.9311 1C30.6278 1 22.2754 9.43111 22.2754 19.8314C22.2754 30.2317 30.6278 38.6628 40.9311 38.6628Z"
            fill="var(--fv-empty-illustration-accent, var(--fv-color-background-info-subtle, #d1e9ff))"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M37.94 34.4431C38.9195 34.6256 39.9159 34.7204 40.9294 34.7276C49.0797 34.7276 55.6868 28.0582 55.6868 19.8311C55.6868 11.604 49.0797 4.93457 40.9294 4.93457C38.8357 4.93457 36.8438 5.37468 35.0396 6.16825C31.9044 7.54729 29.3359 9.99372 27.7846 13.0528C26.7535 15.0861 26.1719 17.39 26.1719 19.8311C26.1719 22.0459 26.6507 24.1478 27.5096 26.0371C28.1228 27.3859 28.9297 28.6263 29.8943 29.7221"
            fill="var(--fv-empty-illustration-surface, var(--fv-color-background-neutral-primary, #fcfcfd))" />
          <path
            d="M37.94 34.4431C38.9195 34.6256 39.9159 34.7204 40.9294 34.7276C49.0797 34.7276 55.6868 28.0582 55.6868 19.8311C55.6868 11.604 49.0797 4.93457 40.9294 4.93457C38.8357 4.93457 36.8438 5.37468 35.0396 6.16825C31.9044 7.54729 29.3359 9.99372 27.7846 13.0528C26.7535 15.0861 26.1719 17.39 26.1719 19.8311C26.1719 22.0459 26.6507 24.1478 27.5096 26.0371C28.1228 27.3859 28.9297 28.6263 29.8943 29.7221"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2"
            stroke-linecap="round" />
          <path
            d="M31.6289 31.3965C32.8252 32.378 34.1766 33.1741 35.6404 33.7416"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2"
            stroke-linecap="round" />
          <path
            d="M55.416 33.9375L59.5864 38.1003"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M59.0464 37.5558C57.9924 38.6197 57.9924 40.3447 59.0464 41.4087L65.2196 47.6401C66.2737 48.7041 67.9826 48.7041 69.0366 47.6401C70.0906 46.5761 70.0906 44.8511 69.0366 43.7872L62.8633 37.5558C61.8093 36.4918 60.1004 36.4918 59.0464 37.5558Z"
            fill="var(--fv-empty-illustration-accent, var(--fv-color-background-info-subtle, #d1e9ff))"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2" />
          <path
            d="M61.8125 38.6631L67.9382 44.8465"
            stroke="var(--fv-empty-illustration-stroke-soft, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2"
            stroke-linecap="round" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M37.3122 13.3669C37.3122 19.8865 42.548 25.1717 49.0068 25.1717C50.2756 25.1717 51.4971 24.9677 52.6411 24.5905C50.7739 29.2679 46.2349 32.5697 40.9319 32.5697C33.9624 32.5697 28.3125 26.8666 28.3125 19.8314C28.3125 13.4566 32.9516 8.17546 39.0079 7.24023C37.9319 9.02682 37.3122 11.1238 37.3122 13.3669Z"
            fill="var(--fv-empty-illustration-surface, var(--fv-color-background-neutral-primary, #fcfcfd))" />
          <path
            d="M41.2117 9.43164C40.5026 9.43164 39.8097 9.50206 39.1397 9.63635M37.1157 10.2614C33.3061 11.8774 30.6309 15.6794 30.6309 20.1121"
            stroke="var(--fv-empty-illustration-stroke-soft, var(--fv-color-content-brand, #1570ef))"
            stroke-width="1.5"
            stroke-linecap="round" />
          <path
            d="M70.8243 19.9871H66.2714M74.3462 15.6152H64.7812H74.3462ZM78.2444 15.6152H77.0075H78.2444Z"
            stroke="var(--fv-empty-illustration-stroke-soft, var(--fv-color-content-brand, #1570ef))"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round" />
          <path
            d="M20.7044 32.3539H16.1514M18.1005 27.4199H8.53556H18.1005ZM5.29213 27.4199H2.94141H5.29213Z"
            stroke="var(--fv-empty-illustration-stroke-soft, var(--fv-color-content-brand, #1570ef))"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      }
      @default {
        <svg viewBox="0 0 92.998 49.0001" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M89.9844 37.001C91.6489 37.001 92.998 38.7106 92.998 40.8193C92.998 42.928 91.6488 44.6377 89.9844 44.6377C88.32 44.6375 86.9708 42.9279 86.9707 40.8193C86.9707 38.7107 88.32 37.0012 89.9844 37.001ZM51.2363 6.45508C52.9007 6.45535 54.25 8.16488 54.25 10.2734C54.2499 12.3819 52.9006 14.0915 51.2363 14.0918H78.791C80.4554 14.0918 81.8055 15.8007 81.8057 17.9092C81.8057 20.0179 80.4555 21.7275 78.791 21.7275H88.2637C89.9281 21.7276 91.2773 23.4373 91.2773 25.5459C91.2773 27.6546 89.9281 29.3642 88.2637 29.3643H80.083C78.4185 29.3643 77.0693 31.0739 77.0693 33.1826C77.0695 35.2912 78.4186 37.001 80.083 37.001H82.666C84.3304 37.001 85.6805 38.7098 85.6807 40.8184C85.6807 42.9271 84.3305 44.6367 82.666 44.6367H60.2773C60.0558 44.6367 59.8398 44.6063 59.6318 44.5488C59.4239 44.6063 59.2079 44.6367 58.9863 44.6367H19.8057C18.1411 44.6367 16.792 42.9271 16.792 40.8184C16.7921 38.7098 18.1412 37.001 19.8057 37.001H3.01367C1.34932 37.0008 0.000115759 35.2911 0 33.1826C0 31.074 1.34925 29.3644 3.01367 29.3643H20.2363C21.9007 29.3641 23.25 27.6545 23.25 25.5459C23.25 23.4373 21.9007 21.7277 20.2363 21.7275H9.47266C7.80814 21.7275 6.45801 20.0179 6.45801 17.9092C6.4582 15.8007 7.80825 14.0918 9.47266 14.0918H26.6943C25.0299 14.0918 23.6807 12.3821 23.6807 10.2734C23.6807 8.16472 25.0298 6.45509 26.6943 6.45508H51.2363Z"
            fill="var(--fv-empty-illustration-bg, var(--fv-color-background-neutral-secondary, #dde1e9))" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M39.9697 32.8037C39.9268 33.1055 39.9045 33.4138 39.9045 33.7274C39.9045 37.3423 42.8575 40.2728 46.5003 40.2728C50.143 40.2728 53.096 37.3423 53.096 33.7274C53.096 33.4138 53.0738 33.1055 53.0308 32.8037H69.0356V47.3637C69.0356 48.2675 68.2974 49.0001 67.3867 49.0001H25.6138C24.7031 49.0001 23.9649 48.2675 23.9649 47.3637V32.8037H39.9697Z"
            fill="var(--fv-empty-illustration-surface, var(--fv-color-background-neutral-primary, #fcfcfd))" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M53.6456 32.6366C53.6456 36.5528 50.4465 39.7275 46.5003 39.7275C42.554 39.7275 39.3549 36.5528 39.3549 32.6366C39.3549 32.5098 39.3583 32.3837 39.3649 32.2584H23.9649L29.2197 16.8396C29.4463 16.1749 30.0746 15.7275 30.7817 15.7275H62.2188C62.9259 15.7275 63.5543 16.1749 63.7808 16.8396L69.0356 32.2584H53.6356C53.6423 32.3837 53.6456 32.5098 53.6456 32.6366Z"
            fill="var(--fv-empty-illustration-surface, var(--fv-color-background-neutral-primary, #fcfcfd))" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M52.6 33.1574C52.6 36.1835 49.869 39.1822 46.5003 39.1822C43.1315 39.1822 40.4006 36.1835 40.4006 33.1574C40.4006 33.0593 40.4034 32.4164 40.4091 32.3197H28.9117L33.3975 21.4961C33.5909 20.9824 34.1273 20.6367 34.7309 20.6367H58.2696C58.8732 20.6367 59.4096 20.9824 59.603 21.4961L64.0889 32.3197H52.5914C52.5971 32.4164 52.6 33.0593 52.6 33.1574Z"
            fill="var(--fv-empty-illustration-accent, var(--fv-color-background-info-subtle, #d1e9ff))" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M24.6489 32.3704V46.8183C24.6489 47.3454 25.0796 47.7728 25.6108 47.7728H67.3837C67.9149 47.7728 68.3456 47.3454 68.3456 46.8183V32.3704L63.127 17.0579C62.9948 16.6702 62.6283 16.4092 62.2158 16.4092H30.7787C30.3663 16.4092 29.9997 16.6702 29.8675 17.0579L24.6489 32.3704Z"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2" />
          <path
            d="M31.6602 32.0908C33.8239 32.0908 36.1589 32.0908 38.6653 32.0908C39.6966 32.0908 39.6966 32.81 39.6966 33.1817C39.6966 36.7967 42.7175 39.7272 46.4441 39.7272C50.1706 39.7272 53.1915 36.7967 53.1915 33.1817C53.1915 32.81 53.1915 32.0908 54.2228 32.0908H67.9366M27.5784 32.0908H28.9119H27.5784Z"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round" />
          <path
            d="M60.2957 3.34695L54.1948 10.1396M46.0049 1V10.1396V1ZM31.6595 3.34695L37.7603 10.1396L31.6595 3.34695Z"
            stroke="var(--fv-empty-illustration-stroke, var(--fv-color-content-brand, #1570ef))"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        line-height: 0;
      }

      svg {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class FvEmptyIllustrationComponent {
  /** Which illustration to show: `data` (empty tray) or `filters` (no search results). */
  readonly variant = input<'data' | 'filters'>('data');
}
